import React from 'react';
import { useSimulationStore } from '@/store/useSimulationStore';
import { useUIStore } from '@/store/useUIStore';
import { formatTokens } from '@/utils/formatters';

export const ActionPanel: React.FC = () => {
  const tasks = useSimulationStore(s => s.tasks);
  const agents = useSimulationStore(s => s.agents);
  const stores = useSimulationStore(s => s.stores);
  const { approveTask, rejectTask } = useSimulationStore();
  const { selectedAgentId } = useUIStore();

  const pendingTasks = tasks.filter(t => t.status === 'waiting_validation');
  const selectedAgent = agents.find(a => a.id === selectedAgentId);

  return (
    <aside className="flex flex-col bg-bg-panel border-t md:border-t-0 md:border-l border-bg-border w-full md:w-[280px] flex-shrink-0 md:h-full">
      {/* ── Validations en attente ──────────────────────────── */}
      <div className="flex flex-col border-b border-bg-border">
        <div className="px-3 py-2 border-b border-bg-border">
          <div className="flex items-center justify-between">
            <span className="font-pixel text-xs text-gray-400">VALIDATIONS</span>
            {pendingTasks.length > 0 && (
              <span className="px-1.5 py-0.5 rounded font-mono text-xs bg-neon-amber/20 text-neon-amber font-bold">
                {pendingTasks.length}
              </span>
            )}
          </div>
        </div>

        <div className="max-h-64 overflow-y-auto scrollbar-thin">
          {pendingTasks.length === 0 ? (
            <div className="px-3 py-6 text-center text-gray-600 font-mono text-xs">
              Aucune validation requise
            </div>
          ) : (
            pendingTasks.map(task => {
              const agent = agents.find(a => a.id === task.agentId);
              const store = stores.find(s => s.id === task.storeId);
              return (
                <div key={task.id} className="px-3 py-3 border-b border-bg-border/40">
                  {/* Agent + boutique */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    {agent && (
                      <span
                        className="font-pixel text-xs"
                        style={{ color: agent.color, fontSize: '6px' }}
                      >
                        {agent.codename}
                      </span>
                    )}
                    {store && (
                      <span className="font-mono text-xs text-gray-500">
                        · {store.emoji} {store.name}
                      </span>
                    )}
                  </div>

                  {/* Titre de tâche */}
                  <p className="font-mono text-xs text-gray-200 leading-relaxed mb-2">
                    {task.title}
                  </p>

                  {/* Tokens utilisés */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-mono text-xs text-gray-600">
                      {formatTokens(task.actualTokens)} tokens
                    </span>
                    <span
                      className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                        task.priority === 'critical'
                          ? 'bg-neon-red/20 text-neon-red'
                          : task.priority === 'high'
                          ? 'bg-neon-orange/20 text-neon-orange'
                          : 'bg-gray-700 text-gray-400'
                      }`}
                    >
                      {task.priority.toUpperCase()}
                    </span>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => approveTask(task.id)}
                      className="flex-1 py-1.5 rounded font-mono text-xs font-bold bg-neon-green/20 text-neon-green border border-neon-green/40 hover:bg-neon-green/30 transition-colors"
                    >
                      ✓ APPROUVER
                    </button>
                    <button
                      onClick={() => rejectTask(task.id)}
                      className="flex-1 py-1.5 rounded font-mono text-xs font-bold bg-neon-red/10 text-neon-red border border-neon-red/30 hover:bg-neon-red/20 transition-colors"
                    >
                      ✗ REJETER
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ── Fiche agent sélectionné ─────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        {selectedAgent ? (
          <AgentQuickView agent={selectedAgent} />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-600">
            <span className="font-mono text-xs">Sélectionne un agent</span>
            <span className="font-mono text-xs">dans le roster ou le plan</span>
          </div>
        )}
      </div>
    </aside>
  );
};

const AgentQuickView: React.FC<{ agent: ReturnType<typeof useSimulationStore.getState>['agents'][0] }> = ({ agent }) => {
  const tasks = useSimulationStore(s => s.tasks);
  const currentTask = tasks.find(t => t.id === agent.currentTaskId);
  const recentTasks = tasks.filter(t => t.agentId === agent.id && t.status === 'done').slice(0, 3);

  return (
    <div className="p-3">
      {/* Header agent */}
      <div className="flex items-center gap-3 mb-3 pb-3 border-b border-bg-border">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center font-pixel text-xs flex-shrink-0"
          style={{ background: agent.color + '20', border: `2px solid ${agent.color}`, color: agent.color, boxShadow: `0 0 10px ${agent.color}40` }}
        >
          {agent.codename.slice(0, 2)}
        </div>
        <div>
          <div className="font-pixel text-xs" style={{ color: agent.color }}>{agent.codename}</div>
          <div className="font-mono text-xs text-gray-400">{agent.roleName}</div>
          <div className="font-mono text-xs text-neon-amber">Lv{agent.stats.level} · {agent.stats.tasksCompleted} tâches</div>
        </div>
      </div>

      {/* Personnalité */}
      <p className="font-mono text-xs text-gray-400 leading-relaxed mb-3 italic">
        "{agent.personality}"
      </p>

      {/* Tâche en cours */}
      {currentTask && (
        <div className="mb-3 p-2 rounded bg-bg-elevated border border-bg-border">
          <div className="font-mono text-xs text-gray-500 mb-1">EN COURS</div>
          <div className="font-mono text-xs text-gray-200">{currentTask.title}</div>
          <div className="mt-1.5 h-1 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${currentTask.progress}%`, background: agent.color }}
            />
          </div>
          <div className="font-mono text-xs text-gray-600 mt-0.5 text-right">{currentTask.progress}%</div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <Stat label="Efficacité" value={`${agent.stats.efficiency}%`} color="#00E676" />
        <Stat label="Tokens/jour" value={`${(agent.tokensBurned / 1000).toFixed(1)}K`} color="#00E5FF" />
      </div>

      {/* Historique récent */}
      {recentTasks.length > 0 && (
        <div>
          <div className="font-mono text-xs text-gray-600 mb-1">RÉCENT</div>
          {recentTasks.map(t => (
            <div key={t.id} className="flex items-center gap-1 py-0.5">
              <span className="text-neon-green font-mono text-xs">✓</span>
              <span className="font-mono text-xs text-gray-500 truncate">{t.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; color: string }> = ({ label, value, color }) => (
  <div className="p-2 rounded bg-bg-elevated border border-bg-border">
    <div className="font-mono text-xs text-gray-600">{label}</div>
    <div className="font-mono text-sm font-bold" style={{ color }}>{value}</div>
  </div>
);
